import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { setCssAttributes } from '@app/finxone-web-frontend/app/lib/utils/utils';
import {
  noNumbersValidator,
  noSpecialCharsValidator,
} from '@app/finxone-web-frontend/app/lib/utils/validators/validators.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';

import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import {
  APP_ZONES,
  BaseCssProperties,
  BaseWidgetProperties,
  LayoutWidgetConfig,
  ProfileStateModel,
  ProviderClass,
  UiZoneWidgetConfig,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { FormActionTypeEnum } from 'libs/shared/enums/form-action.enum';
import { Subject, debounceTime, map, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { FormSubmissionService } from '../../../../lib/services/form-submission-service/form-submission-service.service';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../lib/services/multipart-holder/multipart-form-holder.service';
import { StyleServiceService } from '../../../../lib/services/style-service/style-service.service';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { FormSubmitter } from '../form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-name',
  templateUrl: './name.component.html',
  styleUrls: ['./name.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NameComponent extends FormSubmitter implements OnInit {
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  @Output() formValuesChange = new EventEmitter<any>();
  @Input() formActionStateData: { [key: string]: string };
  @Input() columnStyle = false;
  @Input() dynamicZone = false;
  @Input() fieldAttributesName: any[];
  public zone = 'onboarding';
  public widgetConfig: UiZoneWidgetConfig;
  public totalSteps: number;
  public userProfileData: ProfileStateModel;
  public nameForm: FormGroup;
  public isStepComplete = false;
  public isError = false;
  public showPopup = false;
  public widgetsAttributes: LayoutWidgetConfig[] = [];
  public nameString = '';
  public urlParams: string[] = [];
  public isShowErrorMsg = false;

  public colorTextAlignStyle: baseCssConfigStyle;
  public colorStyle: baseCssConfigStyle;
  public borderStyle: baseCssConfigStyle;
  public nextPageUrl: string;
  public nameFormValues: { [key: string]: string } = {
    first_name: '',
    middle_name: '',
    last_name: '',
  };
  public isFormDataValue = false;
  public isNameShowSuccessIcon = false;
  public isNameShowErrorIcon = false;
  public textInputFontStyle: baseCssConfigStyle;
  public textInputBackgroundColor: baseCssConfigStyle;
  public combinedContentAndBorderStyles: baseCssConfigStyle;
  public textContentStyle: baseCssConfigStyle;
  private isValueChanged = true;

  public inputStyle: baseCssConfigStyle;
  public inputBaseStyle: baseCssConfigStyle;
  public inputInactiveStyle: baseCssConfigStyle;
  public currentInputStyle: { [key: string]: baseCssConfigStyle } = {};
  public fieldAttributes: any[];
  public fieldAttributesObject: Record<string, any>;
  public currentInputStyleFirstName: baseCssConfigStyle;
  public currentInputStyleMiddleName: baseCssConfigStyle;
  public currentInputStyleLastName: baseCssConfigStyle;
  constructor(
    private configService: ConfigService,
    private formHolderService: MultiPartFormHolderService,
    public dataIn: ProviderClass<any>,
    private urlZoneInput: ZonePageUrlProvider,
    protected override store: Store,
    protected _router: Router,
    private styleService: StyleServiceService,
    private formSubmissionService: FormSubmissionService,
    public pageConfig: PageConfigurationProvider,
  ) {
    super(store);
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.fieldAttributes = this.widgetConfig?.attributes['field_attributes'];
      this.attri = this.dataIn.attri;
      this.fieldAttributesObject = this.transformArrayToObject(
        this.widgetConfig?.attributes['field_attributes'],
      );
    }
    this.zone = this.urlZoneInput.zoneUrlInput;
  }

  ngOnInit(): void {
    if (this.dynamicZone && this.fieldAttributesName) {
      this.fieldAttributes = this.fieldAttributesName;
    }
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
    }
    this.userProfileData = this.store.selectSnapshot(ProfileState.getProfile);
    this.setDataInitially();
    this.getOnboardingZoneLayoutConfig();
    this.setCssAttributes();
    this.subscribeToFormAction().subscribe((response) => {
      if (response?.nextPageUrl) this.nextPageUrl = response.nextPageUrl;
      if (response?.type === 'submitStep') {
        this.onSubmit();
      }

      if (response?.formData?.['first_name']) {
        if (this.nameForm.dirty) {
          response.formData['first_name'] = this.nameForm.value.first_name;
          response.formData['middle_name'] = this.nameForm.value.middle_name;
          response.formData['last_name'] = this.nameForm.value.last_name;
        }
        this.patchNameFormValues(response.formData);
      }
    });
    this.checkNameSuccessErrorIconFlag();
  }

  private setDataInitially() {
    let firstNameValue = '';
    let middleNameValue = '';
    let lastNameValue = '';
    if (this.dynamicZone && this.formActionStateData !== undefined) {
      firstNameValue = this.formActionStateData?.['first_name'] ?? '';
      middleNameValue = this.formActionStateData?.['middle_name'] ?? '';
      lastNameValue = this.formActionStateData?.['last_name'] ?? '';
    } else if (
      !this.dynamicZone &&
      this.userProfileData &&
      this.zone !== formatNameAsUrl(APP_ZONES.ONBOARDING)
    ) {
      firstNameValue = this.userProfileData.firstName ?? '';
      middleNameValue = this.userProfileData.middleName ?? '';
      lastNameValue = this.userProfileData.lastName ?? '';
    }
    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.NAME_WIDGET,
      route: '/onboard-individual/register',
      form: new FormGroup({
        first_name: new FormControl(firstNameValue, [
          Validators.required,
          noNumbersValidator(),
          noSpecialCharsValidator(),
        ]),
        middle_name: new FormControl(middleNameValue, [noNumbersValidator(), noSpecialCharsValidator()]),
        last_name: new FormControl(lastNameValue, [
          Validators.required,
          noNumbersValidator(),
          noSpecialCharsValidator(),
        ]),
      }),
    });
    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);
  }

  private checkNameSuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isNameShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isNameShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }
  public setCssAttributes() {
    const { contentStyle, borderStyle, textInputFontStyle, textInputBackgroundColor } = setCssAttributes(
      this.styleService,
      this.configService,
      this.attri,
    );

    this.contentStyle = contentStyle;
    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.TEXTALIGN,
    ]);
    this.borderStyle = borderStyle;
    this.textInputFontStyle = textInputFontStyle;
    this.textInputBackgroundColor = textInputBackgroundColor;
    this.combinedContentAndBorderStyles = {
      ...contentStyle,
      ...borderStyle,
    };

    this.getAndAssignStylesToInputFields(this.attri.widgetProperties);
  }

  private getAndAssignStylesToInputFields(widgetProperties: BaseWidgetProperties) {
    const { baseStyle, activeStyle, inactiveStyle } =
      this.configService.getInputFieldCssStyles(widgetProperties);

    this.inputStyle = baseStyle;
    this.inputBaseStyle = activeStyle;
    this.inputInactiveStyle = inactiveStyle;
    this.widgetConfig.attributes?.['field_attributes']?.forEach((field: any) => {
      this.currentInputStyle[field.name] = {
        ...this.inputInactiveStyle,
        ...this.inputBaseStyle,
      };
    });
  }

  public getOnboardingZoneLayoutConfig() {
    this.buildForm();
  }

  public buildForm() {
    const stage = this.formHolderService.config.getStage(WidgetNames.NAME_WIDGET);
    this.nameForm = stage.getForm();

    this.nameForm.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        map((formValues) => {
          if (
            this.zone === formatNameAsUrl(APP_ZONES.VERIFICATION) &&
            this.nameForm.valid &&
            this.isValueChanged
          ) {
            this.formSubmissionService.storeFormData(this.nameForm.value, FormActionTypeEnum.KYC_DETAILS);
            this.isValueChanged = false;
          } else if (this.nameForm.valid || this.isValueChanged) {
            this.formValuesChange.emit(formValues);
            this.isValueChanged = false;
          }
        }),
      )
      .subscribe();
  }
  public valueChangesTrigger() {
    this.isValueChanged = true;
  }

  public patchNameFormValues(formData: Record<string, string>) {
    this.nameForm.get('first_name')?.setValue(formData?.['first_name']);
    this.nameForm.get('middle_name')?.setValue(formData?.['middle_name']);
    this.nameForm.get('last_name')?.setValue(formData?.['last_name']);
    let name = '';
    Object.values(this.nameFormValues).forEach((value: string) => {
      name = name + value + ' ';
    });
    this.nameString = name;
    this.isStepComplete = !!(formData?.['first_name'] && formData?.['last_name']);

    this.isError = this.nameForm.invalid;
  }

  public onSubmit() {
    const formValues: Record<string, string> = this.nameForm.value;
    if (this.nameForm.valid) {
      this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then((success) => {
        if (success) {
          let name = '';
          Object.values(formValues).forEach((value: string) => {
            name = name + value + ' ';
          });
          this.nameString = name;
          this.isStepComplete = true;
          this.isError = false;
          // Form submission was successful, you can handle it here
        } else {
          // Handle submission failure
        }
      });
    } else {
      this.nameForm.markAllAsTouched();
      this.isStepComplete = false;
      this.isError = true;
      this.showPopup = true;
      this.isCheckValidation();
    }
  }

  public showErrorMsg() {
    this.isShowErrorMsg = !this.isShowErrorMsg;
  }

  public isCheckValidation() {
    const formValues: Record<string, string> = this.nameForm.value;
    this.isFormDataValue = false;
    for (const valKey in this.nameForm.value) {
      if (this.nameForm.value[valKey] !== this.nameFormValues[valKey]) {
        this.isFormDataValue = true;
      }
    }
    if (this.isFormDataValue) {
      this.formSubmissionService
        .submitIndividualForm(formValues, this.nextPageUrl)
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .then(() => {});
    }
  }

  public updateInputFieldCurrentStyles(event: boolean, fieldName: string) {
    if (event) {
      this.currentInputStyle[fieldName] = {
        ...this.inputStyle,
        ...this.inputBaseStyle,
      };
    } else {
      this.currentInputStyle[fieldName] = {
        ...this.inputInactiveStyle,
        ...this.inputBaseStyle,
      };
    }
  }

  private transformArrayToObject(array: any[]) {
    return array?.reduce((acc, item) => {
      acc[item.name] = item;
      return acc;
    }, {});
  }
}
